import { assertNotEmpty, type Optional } from '@festina-life/core'

import { isSpecialCasedSubTemplatedValueSetup } from '../components/specialCasedSubTemplatedViewers/types'
import type { ComparableField } from '../types/diffing'
import type { MetaModel } from '../types/metaModel'
import {
  type AbstractTemplate,
  assertSubTemplatedField,
  type DeclaredConfigurationTemplateFunctionValue,
  type DynamicConfigurationTemplateFunctionValue,
  type SubTemplateValueSetupFieldValue,
  type TemplateField,
  type TemplateVersion,
} from '../types/templateModel'
import { generateEmptyFieldValue } from '../uiModels/createEmptyFieldHelpers'
import {
  isFunctionPathDeclaration,
  splitPartialTemplatePath,
  splitPathOnId,
} from './pathHelpers'

const findMatchingFunctionInTemplate = ({
  functionIdentifier,
  template,
  shouldSkipEmpty,
}: {
  functionIdentifier: string
  template: AbstractTemplate | TemplateVersion
  shouldSkipEmpty: boolean
}): Optional<
  | DeclaredConfigurationTemplateFunctionValue
  | DynamicConfigurationTemplateFunctionValue
> =>
  [
    ...(template?.declaredFunctions ?? []),
    ...(template.dynamicFunctions ?? []),
  ].find((templateFunction) => {
    const isMatchingPath =
      templateFunction.functionIdentifier === functionIdentifier

    return shouldSkipEmpty
      ? isMatchingPath && templateFunction.function?.fplusSource
      : isMatchingPath
  })

const findMatchingFieldInTemplate = ({
  fieldName,
  template,
  shouldSkipEmpty,
}: {
  fieldName: string
  template: AbstractTemplate | TemplateVersion
  shouldSkipEmpty: boolean
}): Optional<TemplateField> =>
  template.fields.find((field) => {
    const isMatchingPath = field.name === fieldName

    return shouldSkipEmpty ? isMatchingPath && !!field.value : isMatchingPath
  })

const getPathFromTemplate = <T extends ComparableField>({
  path,
  version,
  shouldSkipEmpty,
  metaModel,
}: {
  path: string
  version: AbstractTemplate | TemplateVersion
  shouldSkipEmpty: boolean
  metaModel: MetaModel
}): Optional<T> => {
  const isFunctionPath = isFunctionPathDeclaration(path)

  if (isFunctionPath) {
    return findMatchingFunctionInTemplate({
      functionIdentifier: path.substring(1),
      template: version,
      shouldSkipEmpty,
    }) as T
  } else {
    const { field, remainingPath } = splitPartialTemplatePath(path)

    if (!remainingPath && !field.subTemplateId) {
      return findMatchingFieldInTemplate({
        fieldName: path.substring(1),
        template: version,
        shouldSkipEmpty,
      }) as T
    }

    const propertyName = field.fieldName
    assertNotEmpty(propertyName)

    const subTemplateId = field.subTemplateId

    const subtemplateField = version.fields.find(
      (field) => field.name === propertyName,
    )

    if (!subtemplateField) {
      return null
    }

    assertSubTemplatedField(subtemplateField)

    const subTemplatedFieldValue =
      subtemplateField.value ??
      (generateEmptyFieldValue({
        field: subtemplateField,
        meta: metaModel,
      }) as SubTemplateValueSetupFieldValue)

    const variants = subTemplatedFieldValue?.variants ?? []

    const indexedVariant = (variants ?? []).find(
      (variant) => variant.subTemplateIdentifier === subTemplateId,
    )

    const isRemainingPathAVariantProperty =
      !!remainingPath &&
      ['.defaultSelected', '.selectable'].includes(remainingPath)

    if (!remainingPath) {
      return indexedVariant as Optional<T>
    } else if (isRemainingPathAVariantProperty) {
      return (
        indexedVariant?.[
          remainingPath.substring(1) as 'defaultSelected' | 'selectable'
        ]
          ? indexedVariant
          : null
      ) as Optional<T>
    }

    return indexedVariant
      ? getPathFromTemplate({
          path: remainingPath,
          version: indexedVariant,
          shouldSkipEmpty,
          metaModel,
        })
      : null
  }
}

export const getPathValue = <T extends ComparableField>({
  path,
  isEnforcingSameTemplate = true,
  shouldSkipEmpty,
  version,
  metaModel,
}: {
  path: string
  version: TemplateVersion
  isEnforcingSameTemplate?: boolean
  shouldSkipEmpty: boolean
  metaModel: MetaModel
}): Optional<T> => {
  const splitPathResolved = splitPathOnId(path)

  if (!splitPathResolved) {
    return null
  }

  const [templateId, propertyPath] = splitPathResolved

  if (
    isEnforcingSameTemplate &&
    templateId !== version.versionInfo.identifier
  ) {
    return null
  }

  if (!propertyPath) {
    return version as T
  }

  const valueFromTemplate = getPathFromTemplate<T>({
    path: propertyPath,
    version,
    shouldSkipEmpty,
    metaModel,
  })

  const origin =
    valueFromTemplate && isSpecialCasedSubTemplatedValueSetup(valueFromTemplate)
      ? valueFromTemplate.field.origin
      : valueFromTemplate?.origin

  // Empty values never have an origin inside of them given that the values aren't in a version.
  // For the sake of diffing and knowing which version the value comes from, the origin is added here for
  // matched values that are returning an empty value setup value.
  if (valueFromTemplate && !shouldSkipEmpty && !origin) {
    return { ...valueFromTemplate, origin: path }
  }

  return valueFromTemplate
}

export const getInheritedValue = <T extends ComparableField>({
  path,
  metaModel,
  hierarchy,
}: {
  path: string
  hierarchy: TemplateVersion[]
  metaModel: MetaModel
}): Optional<T> => {
  const invertedHierarchy = hierarchy.toReversed()

  let inheritedValue: Optional<T> = null
  invertedHierarchy.forEach((hierarchyVersion) => {
    if (!inheritedValue) {
      inheritedValue = getPathValue<T>({
        path,
        version: hierarchyVersion,
        isEnforcingSameTemplate: false,
        shouldSkipEmpty: true,
        metaModel,
      })
    }
  })

  return inheritedValue
}
